import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack, BiFile, BiReceipt, BiTimeFive } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import { activeImportValues, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';
import './CustomerMaster.scss';

const EMPTY = {
  serviceNo: '',
  code: '',
  name: '',
  category: '',
  billingBasis: '',
  typicalDuration: '',
  deliverable: '',
  description: '',
  notes: '',
  status: 'active'
};

const monogram = (form) => {
  const source = String(form.code || form.name || '').trim();
  if (!source) return 'New';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 4).toUpperCase();
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const ServiceFormPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const { serviceId } = useParams();
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [billing, setBilling] = useState([]);
  const [listsReady, setListsReady] = useState(false);
  const revision = useMasterRevision({ entity: 'service' });

  useDocumentTitle(isCreate ? 'New service' : (form.name || 'Service'));

  useEffect(() => {
    let cancelled = false;
    loadOrganisationSettings().then((data) => {
      if (cancelled) return;
      const lists = data?.settings?.importLists || {};
      const nextCategories = activeImportValues(lists.serviceCategories);
      const nextBilling = activeImportValues(lists.serviceBilling);
      setCategories(nextCategories);
      setBilling(nextBilling);
      setListsReady(true);
      if (isCreate) {
        setForm((prev) => ({
          ...prev,
          category: nextCategories.includes(prev.category) ? prev.category : (nextCategories[0] || ''),
          billingBasis: nextBilling.includes(prev.billingBasis) ? prev.billingBasis : (nextBilling[0] || '')
        }));
      }
    });
    return () => { cancelled = true; };
  }, [isCreate]);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await parivartanAPI.getService(serviceId);
        const pack = response.data?.data || {};
        const row = pack.service;
        if (!cancelled && Array.isArray(pack.categories) && pack.categories.length) {
          setCategories(pack.categories.map((item) => String(item || '').trim()).filter(Boolean));
        }
        if (!cancelled && Array.isArray(pack.billing) && pack.billing.length) {
          setBilling(pack.billing.map((item) => String(item || '').trim()).filter(Boolean));
        }
        if (!cancelled && row) {
          setForm({
            ...EMPTY,
            ...row,
            serviceNo: row.serviceNo || '',
            code: row.code || '',
            typicalDuration: row.typicalDuration || '',
            deliverable: row.deliverable || '',
            description: row.description || '',
            notes: row.notes || ''
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load this service');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serviceId, isCreate]);

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.serviceNo) delete payload.serviceNo;
      if (!payload.code) delete payload.code;
      if (!isCreate) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) return;
        payload.changeReason = changeReason;
      }
      if (isCreate) await parivartanAPI.createService(payload);
      else await parivartanAPI.updateService(serviceId, payload);
      navigate('/apps/tender/masters/services');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this service');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) {
    return (
      <div className="cust-page cust-page--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const mark = monogram(form);
  const heading = isCreate ? 'New service' : (form.name || 'Service');
  const locked = isView;

  return (
    <div className="cust-page">
      <form id="service-master-form" className={`cust-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters/services')}>
              <BiArrowBack />
              Services
            </button>
            <div className="cust-mast__row">
              <span className={`cust-mark${mark.length > 2 ? ' cust-mark--wide' : ''}`} aria-hidden>{mark}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'Service master' : isEdit ? 'Editing service' : 'Service master'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  {form.serviceNo ? <span className="cust-chip cust-chip--no">{form.serviceNo}</span> : <span className="cust-chip">Number on save</span>}
                  {form.code ? <span className="cust-chip">{form.code}</span> : null}
                  {form.category ? <span className="cust-chip">{form.category}</span> : null}
                  <span className={`cust-status cust-status--${form.status === 'active' ? 'active' : 'inactive'}`}>
                    {form.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/masters/services/${serviceId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" onClick={() => navigate('/apps/tender/masters/services')} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isCreate ? 'Create service' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>The service</h2>
                  <p>How this offering is named when a tender is captured.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Name" wide="8">
                  <Form.Control value={form.name} onChange={setField('name')} required disabled={locked} placeholder="Detailed Project Report" />
                </Field>
                <Field label="Service no." wide="4">
                  <Form.Control className="cust-input--code" value={form.serviceNo} onChange={setField('serviceNo')} placeholder="Assigned on save" disabled={locked} />
                </Field>
                <Field label="Code" wide="4">
                  <Form.Control className="cust-input--code" value={form.code} onChange={setField('code')} disabled={locked} placeholder="DPR" />
                </Field>
                <Field label="Category" wide="4">
                  <Form.Select value={form.category} onChange={setField('category')} disabled={locked || !listsReady} required>
                    {!categories.length ? <option value="">{listsReady ? 'Add categories under Settings → Organisation' : 'Loading…'}</option> : null}
                    {keepCurrent(categories, form.category, categories).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label="Status" wide="4">
                  <Form.Select value={form.status} onChange={setField('status')} disabled={locked}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Commercial shape</h2>
                  <p>How the appointment is usually priced and how long it runs.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Billing basis" wide="4">
                  <Form.Select value={form.billingBasis} onChange={setField('billingBasis')} disabled={locked || !listsReady} required>
                    {!billing.length ? <option value="">{listsReady ? 'Add billing bases under Settings → Organisation' : 'Loading…'}</option> : null}
                    {keepCurrent(billing, form.billingBasis, billing).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label="Typical duration" wide="4">
                  <Form.Control value={form.typicalDuration} onChange={setField('typicalDuration')} disabled={locked} placeholder="12–36 months" />
                </Field>
                <Field label="Principal deliverable" wide="4">
                  <Form.Control value={form.deliverable} onChange={setField('deliverable')} disabled={locked} placeholder="What the authority receives" />
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>03</span>
                <div>
                  <h2>Scope</h2>
                  <p>What Dhruv actually does under this service.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Description" wide="12">
                  <Form.Control as="textarea" rows={3} value={form.description} onChange={setField('description')} disabled={locked} placeholder="The work this service covers" />
                </Field>
                <Field label="Notes" wide="12">
                  <Form.Control as="textarea" rows={3} value={form.notes} onChange={setField('notes')} disabled={locked} placeholder="Where this service is usually bid, or when to leave it inactive" />
                </Field>
              </div>
            </section>
          </div>

          <aside className="cust-rail">
            <div className="cust-rail__card">
              <p className="cust-rail__label">At a glance</p>
              <h2>{form.code || form.name || 'New service'}</h2>
              <p className="cust-rail__place">{form.category || 'Category not set'}</p>
              <ul>
                <li>
                  <BiReceipt aria-hidden />
                  <span>{form.billingBasis || 'Billing not set'}</span>
                </li>
                <li>
                  <BiTimeFive aria-hidden />
                  <span>{form.typicalDuration || 'Duration not set'}</span>
                </li>
                <li>
                  <BiFile aria-hidden />
                  <span>{form.deliverable || 'No deliverable yet'}</span>
                </li>
              </ul>
            </div>
            {form.description ? (
              <div className="cust-rail__card cust-rail__notes">
                <p className="cust-rail__label">Scope</p>
                <p>{form.description}</p>
              </div>
            ) : null}
          </aside>
        </div>
      </form>
      {revision.modals}
    </div>
  );
};

export default ServiceFormPage;
